// src/components/AccountDisabledOverlay.js
//
// Full-screen, non-dismissible overlay that blocks the UI when the
// current user's account has been disabled by an administrator.
//
// This is the user-facing counterpart of the admin "Disable Account"
// action.  It renders above every other layer (including SessionTimeout)
// so that a disabled user cannot interact with anything except the
// "Sign Out" button.
//
// The overlay appears within seconds of the admin action thanks to the
// onSnapshot listener in useAccountStatusMonitor.  The user cannot
// navigate away, close the overlay, or continue working -- the only
// available action is to sign out.

import React from 'react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { useAuth } from '../hooks/useAuth';

export default function AccountDisabledOverlay({ isDisabled }) {
    const { signOut } = useAuth();

    if (!isDisabled) return null;

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Sign-out failed:', error);
            // Force a page reload as a last resort so the user is not
            // permanently stuck on a non-functional screen.
            window.location.reload();
        }
    };

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="account-disabled-title"
            aria-describedby="account-disabled-desc"
        >
            <div className="bg-white rounded-lg shadow-xl max-w-md w-[calc(100vw-2rem)] p-8 text-center space-y-4">
                <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                    <ExclamationIcon className="h-7 w-7 text-red-600" />
                </div>

                <h2
                    id="account-disabled-title"
                    className="text-lg font-bold text-gray-900"
                >
                    Account Disabled
                </h2>

                <p
                    id="account-disabled-desc"
                    className="text-sm text-gray-600"
                >
                    Your account has been disabled. Please contact your
                    organisation administrator if you believe this is an error.
                </p>

                <button
                    onClick={handleSignOut}
                    className="px-6 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                    Sign Out
                </button>
            </div>
        </div>
    );
}
